import { Head, Link, usePage } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AuthLayout from "@/layouts/auth-layout"
import {
  newPartnerUserSessionPath,
  resendPartnerUserInvitationPath,
} from "@/routes"

function csrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

export default function InvitationError() {
  // IMP-255: a pending partner invitee gets a resend_token so they can request
  // a fresh invitation themselves; a truly invalid token gets null.
  const { resend_token } = usePage().props as unknown as {
    resend_token: string | null
  }

  const [state, setState] = useState<"idle" | "sending" | "sent">("idle")

  async function requestNewLink() {
    if (!resend_token) return
    setState("sending")
    try {
      await fetch(resendPartnerUserInvitationPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ token: resend_token }),
      })
    } finally {
      // Always land on "sent" — the endpoint deliberately doesn't reveal
      // whether the token mapped to a real invite.
      setState("sent")
    }
  }

  return (
    <AuthLayout title="" description="">
      <Head title="Invitation invalid" />
      <div className="flex w-full flex-col gap-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">
              {resend_token
                ? "Your invitation link expired"
                : "Invitation invalid"}
            </CardTitle>
            <CardDescription>
              {resend_token
                ? state === "sent"
                  ? "Done — if that link belonged to an invite, a fresh one is on its way. Check your inbox."
                  : "Invitation links expire for security. Request a new one and we’ll email it to you right away."
                : "This invitation link is invalid, has already been used, or has expired. Please contact your partner administrator for a new invitation."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            {resend_token && state !== "sent" ? (
              <Button
                onClick={() => void requestNewLink()}
                disabled={state === "sending"}
                loading={state === "sending"}
              >
                {state === "sending" ? "Sending…" : "Email me a new link"}
              </Button>
            ) : (
              <Button variant="outline" asChild className="w-full">
                <Link href={newPartnerUserSessionPath()}>Go to login</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  )
}
